import { NonTimezoneSensitiveDate } from '@commenda/utils';
import { FilterConfig, TableFilter } from '../types';

export function hasActiveFilters(
  filterConfig?: FilterConfig
): filterConfig is FilterConfig {
  if (!filterConfig?.filters || filterConfig.filters.length === 0) return false;

  const activeFilters = filterConfig.activeFilters || {};
  return Object.keys(activeFilters).some((key) => {
    const v = activeFilters[key];
    return (
      (Array.isArray(v) && v.length > 0) || (typeof v === 'string' && v !== '')
    );
  });
}

export function applyFilters<TData>(
  data: TData[],
  filterConfig?: FilterConfig
): TData[] {
  if (
    filterConfig?.isClientFilteringDisabled ||
    !hasActiveFilters(filterConfig)
  )
    return data;

  const activeFilters = filterConfig.activeFilters || {};

  const filterRow = (row: TData, filter: TableFilter): boolean => {
    const filterValue = (activeFilters as Record<string, string | string[]>)[
      filter.columnId
    ];
    if (!filterValue) return true;

    const cellValue = (row as unknown as Record<string, unknown>)[
      filter.columnId
    ] as unknown;

    // Use Record<string, unknown> to type row for key access
    if (
      cellValue === undefined &&
      !(filter.columnId in (row as Record<string, unknown>))
    ) {
      return true;
    }

    if (Array.isArray(filterValue)) {
      if (filterValue.length === 0) return true;
      // A cell can hold multiple values (e.g. the plans a row belongs to); match if any is selected.
      if (Array.isArray(cellValue))
        return cellValue.some((value) => filterValue.includes(String(value)));
      return filterValue.includes(String(cellValue));
    }

    if (filter.type === 'DATE') {
      const toCalendarDate = (value: string): string => {
        const trimmed = value.trim();
        const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoMatch) {
          return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
        }
        try {
          const parsed = new Date(trimmed);
          if (Number.isNaN(parsed.getTime())) return '';
          return NonTimezoneSensitiveDate.fromNativeDate(
            parsed
          ).toISO8601String();
        } catch {
          return '';
        }
      };

      const cellDate = toCalendarDate(String(cellValue));
      // If cell value couldn't be parsed, exclude it from results.
      if (!cellDate) return false;

      const selectedDate = toCalendarDate(String(filterValue));
      // If the selected date can't be parsed, don't filter anything out.
      if (!selectedDate) return true;

      return cellDate === selectedDate;
    }

    if (filter.type === 'DATE_RANGE') {
      const raw = String(filterValue);

      // Parse cell value to ISO date string
      const cellISO = (() => {
        try {
          const cellDate = String(cellValue);
          // Try parsing as date - handle various formats
          const parsedDate = new Date(cellDate);
          if (Number.isNaN(parsedDate.getTime())) {
            // If parsing fails, return empty string to skip this row
            return '';
          }
          const n = NonTimezoneSensitiveDate.fromNativeDate(parsedDate);
          return n.toISO8601String();
        } catch {
          // If any error occurs, skip this row
          return '';
        }
      })();

      // If cell value couldn't be parsed, exclude it from results
      if (!cellISO) return false;

      if (raw.startsWith('custom:')) {
        const [, rangeStr] = raw.split(':');
        const [fromStr, toStr] = rangeStr.split(',');

        if (!fromStr || !toStr) return true;

        try {
          const fromISO = NonTimezoneSensitiveDate.fromNativeDate(
            new Date(fromStr.trim())
          ).toISO8601String();
          const toISO = NonTimezoneSensitiveDate.fromNativeDate(
            new Date(toStr.trim())
          ).toISO8601String();

          return cellISO >= fromISO && cellISO <= toISO;
        } catch (error) {
          return true;
        }
      }

      if (raw.startsWith('preset:')) {
        const code = raw.replace('preset:', '');
        const now = NonTimezoneSensitiveDate.fromNativeDate(new Date());

        const computeRangeISO = (
          c: string
        ): { fromISO: string; toISO: string } | null => {
          const toISO = now.toISO8601String();
          if (/^\d+$/.test(c)) {
            const n = parseInt(c, 10);
            if (Number.isFinite(n) && n > 0) {
              const from = now.subtract(n, 'days');
              return { fromISO: from.toISO8601String(), toISO };
            }
            return null;
          }
          switch (c) {
            case 'mtd': {
              const from = now.startOf('month');
              return { fromISO: from.toISO8601String(), toISO };
            }
            case 'pm': {
              const prev = now.subtract(1, 'month');
              const from = prev.startOf('month');
              const to = prev.endOf('month');
              return {
                fromISO: from.toISO8601String(),
                toISO: to.toISO8601String(),
              };
            }
            case 'ytd': {
              const from = now.startOf('year');
              return { fromISO: from.toISO8601String(), toISO };
            }
            default:
              return null;
          }
        };

        const range = computeRangeISO(code);
        if (!range) return true;
        return cellISO >= range.fromISO && cellISO <= range.toISO;
      }

      return true;
    }

    return String(cellValue) === String(filterValue);
  };

  let filtered = data;
  filterConfig.filters.forEach((filter) => {
    filtered = filtered.filter((row) => filterRow(row, filter));
  });
  return filtered;
}
